/**
 * JsonInput — monospace textarea for editing a workflow trigger payload,
 * pre-filled with an example derived from the workflow's JSON Schema.
 *
 * Why not a field-by-field form?
 *   Power users (the primary audience for "Trigger" on a deployed pipeline)
 *   are devs who already know JSON; pasting a known-good payload from
 *   their last run is faster than tabbing through inputs. The schema is
 *   already available — we use it to seed the example so they're not
 *   starting from a blank textarea.
 *
 *   For non-devs, the form-renderer variant lives next door in SchemaForm.js
 *   and can be opted into later via a tab toggle if there's demand.
 *
 * Validation:
 *   The only client-side check is "does it parse as JSON" — the server
 *   is authoritative for schema validation. We report `ok=false` if the
 *   textarea contents don't parse; the parent disables submit.
 *
 * Syntax highlight:
 *   True JSON-token highlighting needs a tokenizer + DOM overlay (or a
 *   library like prism / shiki / monaco). For the trigger modal that's
 *   overkill — we use a monospace font + bracket-matching CSS, which
 *   gets 80% of the readability for 0 deps. If demand grows we swap in
 *   `react-simple-code-editor` + `prismjs` in this same component.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { schemaToExample, prettyJson } from './schema-example';
import './JsonInput.css';

export default function JsonInput({
  schema,         // JSON Schema (workflow.graph.stateSchema). Optional.
  onChange,       // (parsedValue, { ok, error }) => void
  disabled = false,
  initialMode = 'full', // 'full' (every field) or 'required' (minimal)
  initialValue,   // Optional plain JS object. When provided, takes
                  // precedence over schema-derived example (e.g. the
                  // user's saved `defaultInput` from the workflow row).
}) {
  // Seed precedence: user-saved defaultInput (initialValue) → schema-derived example.
  // Empty object initialValue is allowed (means "user wants empty default").
  const seedFromProps = () => {
    if (initialValue !== undefined && initialValue !== null) {
      return prettyJson(initialValue);
    }
    return prettyJson(schemaToExample(schema, { mode: initialMode }));
  };
  const [text, setText] = useState(seedFromProps);
  const [parseError, setParseError] = useState(null);
  const taRef = useRef(null);

  // Re-seed when schema or initialValue arrives async (e.g. modal opened
  // before the workflow row finished loading). We only overwrite if the
  // user hasn't typed yet — heuristic: text matches the previous example.
  const seededRef = useRef(text);
  useEffect(() => {
    const fresh = seedFromProps();
    if (text === seededRef.current) {
      setText(fresh);
      seededRef.current = fresh;
      validateAndReport(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, initialValue]);

  const validateAndReport = useCallback((nextText) => {
    if (!nextText.trim()) {
      setParseError(null);
      onChange?.({}, { ok: true, error: null });
      return;
    }
    try {
      const parsed = JSON.parse(nextText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        const err = 'Input must be a JSON object (e.g. { "key": "value" }).';
        setParseError(err);
        onChange?.(null, { ok: false, error: err });
        return;
      }
      setParseError(null);
      onChange?.(parsed, { ok: true, error: null });
    } catch (e) {
      const err = e.message.replace(/^JSON\.parse: /, '');
      setParseError(err);
      onChange?.(null, { ok: false, error: err });
    }
  }, [onChange]);

  // Initial report so parent's `ok` reflects the seeded state.
  useEffect(() => { validateAndReport(text); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const next = e.target.value;
    setText(next);
    validateAndReport(next);
  };

  // Tab key inserts 2 spaces instead of moving focus.
  const handleKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      const ta = taRef.current;
      if (!ta) return;
      const { selectionStart: start, selectionEnd: end } = ta;
      const next = `${text.slice(0, start)}  ${text.slice(end)}`;
      setText(next);
      validateAndReport(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 2;
      });
    }
  };

  // Schema-driven field reference rendered above the textarea so users
  // see what each JSON key means without leaving the modal. Cheap
  // alternative to the deleted "Schema reference (raw JSON dump)" — we
  // pull `name`, `type`, and `description` only. Order matches the
  // schema's properties iteration order, which JsonSchema preserves.
  const fields = schema?.properties
    ? Object.entries(schema.properties).map(([name, def]) => ({
        name,
        type: def.type || (def.enum ? 'enum' : 'any'),
        required: Array.isArray(schema.required) && schema.required.includes(name),
        description: def.description || '',
      }))
    : [];

  return (
    <div className="json-input">
      <div className="json-input-toolbar">
        <span className="json-input-toolbar-label">Trigger payload (JSON)</span>
      </div>

      <textarea
        ref={taRef}
        className={`json-input-textarea ${parseError ? 'has-error' : ''}`}
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        rows={Math.min(20, Math.max(6, text.split('\n').length + 1))}
        placeholder='{ "key": "value" }'
        aria-invalid={Boolean(parseError)}
      />

      {parseError && (
        <p className="json-input-error">⚠ {parseError}</p>
      )}

      {fields.length > 0 && (
        <ul className="json-input-fields" aria-label="Field reference">
          {fields.map((f) => (
            <li key={f.name} className="json-input-field">
              <code className="json-input-field-name">{f.name}</code>
              <span className="json-input-field-meta">
                {f.type}{f.required ? ', required' : ', optional'}
              </span>
              {f.description && (
                <span className="json-input-field-desc">— {f.description}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
